// NPC spawning: LLM creator call + deterministic procedural fallback.
// Fallback keeps the game playable offline / on LLM failure.
import { z } from 'zod';
import { TRAIT_SEEDS } from '../db/traits.js';
import { chatComplete } from '../llm/client.js';
import { buildSpawnPrompt, parseJsonLoose, SpawnSchema } from '../llm/prompts.js';
import type { EmotionalState, Mood, Nature, Npc, NurtureTrait } from '../types.js';

const FIRST = ['Mara', 'Jonas', 'Priya', 'Tomas', 'Aiko', 'Ruth', 'Devon', 'Sable', 'Ellis', 'Noor', 'Wren', 'Otto', 'Lena', 'Milo', 'Iris', 'Cato', 'June', 'Rex', 'Anya', 'Solveig', 'Tadeo', 'Isolde', 'Kenji', 'Odette', 'Bram', 'Zadie', 'Cosmo', 'Ines', 'Rufus', 'Saskia', 'Emeka', 'Yuki', 'Petra', 'Dario', 'Anouk', 'Felix', 'Greta', 'Hugo', 'Imani', 'Jasper'];
const LAST = ['Vane', 'Okafor', 'Lindqvist', 'Marsh', 'Tanaka', 'Hale', 'Reyes', 'Quill', 'Frost', 'Haddad', 'Merritt', 'Solace', 'Brandt', 'Kessler', 'Duarte', 'Ashworth', 'Blackwood', 'Calloway', 'Drummond', 'Ellery', 'Fairbanks', 'Grimaldi', 'Holloway', 'Iversen', 'Kowalski', 'Larkspur', 'Moreau', 'Nightingale', 'Osei', 'Pemberton', 'Quimby', 'Renshaw', 'Stanton', 'Thibault', 'Underwood', 'Vasquez', 'Whitlock', 'Yamada', 'Zielinski'];
const PALETTES = [
  { skin: '#f1c27d', shirt: '#4cc9f0', pants: '#3a506b', hair: '#3b2f2f' },
  { skin: '#e0ac69', shirt: '#f72585', pants: '#22223b', hair: '#111111' },
  { skin: '#8d5524', shirt: '#80ed99', pants: '#5c5c5c', hair: '#0d0d0d' },
  { skin: '#ffdbac', shirt: '#ff9e00', pants: '#6d6875', hair: '#b08968' },
  { skin: '#c68642', shirt: '#b5179e', pants: '#283618', hair: '#432818' },
];

const rnd = (n: number): number => Math.floor(Math.random() * n);
const r15 = (): number => 3 + rnd(6); // 3-8 per Nature rules
// Neurotic fallback: ~35% of values snap to the poles instead of the middle.
const rHot = (): number => (Math.random() < 0.35 ? [1, 2, 9, 10][rnd(4)] : r15());

export function flipNeurotic(): boolean {
  return Math.random() < 0.5; // 50/50 wild card per NPC
}

function fallbackNature(neurotic: boolean): Nature {
  const v = (): number => (neurotic ? rHot() : r15());
  return {
    sociability: v(), agreeableness: v(), emotionality: v(),
    caution: v(), discipline: v(), dominance: v(),
    empathy: v(), curiosity: v(), drive: v(),
    sexual_drive: v(), casual_sex_openness: v(),
  };
}

function fallbackNurture(neurotic: boolean): NurtureTrait[] {
  const picks = new Set<number>();
  while (picks.size < 4) picks.add(rnd(TRAIT_SEEDS.length));
  return [...picks].map((i) => {
    const [trait_key] = TRAIT_SEEDS[i];
    return { trait_key, strength: neurotic ? 6 + rnd(5) : 3 + rnd(7), reason: 'Life taught this the hard way over many years.' };
  });
}

const MOODS: Mood[] = ['neutral', 'happy', 'content', 'anxious', 'hopeful', 'lonely'];

// Pick a first+last combo not already taken in this world (best effort).
export function uniqueName(taken: Set<string>): { first: string; last: string } {
  for (let t = 0; t < 40; t++) {
    const first = FIRST[rnd(FIRST.length)];
    const last = LAST[rnd(LAST.length)];
    if (!taken.has(`${first} ${last}`)) return { first, last };
  }
  // Pools exhausted: allow a repeat rather than looping forever.
  return { first: FIRST[rnd(FIRST.length)], last: LAST[rnd(LAST.length)] };
}

export function proceduralNpc(id: string, neurotic = flipNeurotic(), takenNames: Set<string> = new Set()): Npc {
  const pal = PALETTES[rnd(PALETTES.length)];
  const state: EmotionalState = { mood: MOODS[rnd(MOODS.length)], stress: 1 + rnd(10), energy: 1 + rnd(10) };
  const { first, last } = uniqueName(takenNames);
  takenNames.add(`${first} ${last}`);
  return {
    id,
    firstName: first,
    lastName: last,
    palette: { ...pal },
    nature: fallbackNature(neurotic),
    nurture: fallbackNurture(neurotic),
    state,
    savedFavorite: false,
    origin: 'offline',
    neurotic,
  };
}

// LLM spawn via the configured client (configureLlm first).
// Two attempts: if the first reply fails validation, retry once with the
// zod issues fed back so the model repairs ONLY those. Throws on double
// failure (caller falls back to procedural, keeping the same coin flip).
export async function spawnNpcViaLlm(id: string, neurotic = flipNeurotic(), takenNames: string[] = []): Promise<Npc> {
  const base = buildSpawnPrompt(neurotic, takenNames);
  const first = await chatComplete([{ role: 'user', content: base }], {
    json: true,
    maxTokens: 1600, // room for the full vocabulary echo + JSON
    temperature: 1.0,
  });
  const attempt = tryParseSpawn(first);
  if (attempt.ok) return toNpc(id, attempt.value, neurotic);
  const second = await chatComplete(
    [{ role: 'user', content: `${base}\nYour previous reply failed validation:\n${attempt.issues}\nReply again with ONLY corrected JSON.` }],
    { json: true, maxTokens: 1600, temperature: 0.7 },
  );
  return toNpc(id, SpawnSchema.parse(parseJsonLoose(second)), neurotic); // throws → fallback
}

type SpawnValue = z.infer<typeof SpawnSchema>;

function tryParseSpawn(text: string): { ok: true; value: SpawnValue } | { ok: false; issues: string } {
  try {
    const r = SpawnSchema.safeParse(parseJsonLoose(text));
    if (r.success) return { ok: true, value: r.data };
    return {
      ok: false,
      issues: r.error.issues.slice(0, 5).map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    };
  } catch (e) {
    return { ok: false, issues: e instanceof Error ? e.message : 'invalid JSON' };
  }
}

function toNpc(id: string, parsed: SpawnValue, neurotic: boolean): Npc {
  return {
    id,
    firstName: parsed.first_name,
    lastName: parsed.last_name,
    palette: parsed.palette,
    nature: parsed.nature,
    nurture: parsed.nurture,
    state: { mood: parsed.state.mood as Mood, stress: parsed.state.stress, energy: parsed.state.energy },
    savedFavorite: false,
    origin: 'llm',
    neurotic,
  };
}

export function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;
}
